import axios from 'axios';
import store from '../store';
import { logout } from '../actions/auth';
import { getAccessToken } from './authToken';
import { clearCachedAccessStatus } from './accessStatusCache';
import { refreshAuthTokens, shouldRefreshAccessToken, wasExplicitLogout } from './authSession';
import { getActionableErrorMessage, isLikelyTimeoutError } from './networkUx';
import { hardLoginRedirect, withIncomingSearch } from './landingQuery';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api.dopletech.com',
  withCredentials: true,
  xsrfCookieName: 'dople_csrf',
  xsrfHeaderName: 'X-CSRFToken',
});

function getCookie(name) {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
}

function isRefreshRequest(config) {
  return (config?.url || '').includes('/auth/jwt/refresh/');
}

function isLogoutRequest(config) {
  return (config?.url || '').includes('/auth/jwt/logout/');
}

function isPublicAuthRequest(config) {
  const url = config?.url || '';
  return (
    /\/auth\/jwt\/create\/?$/.test(url)
    || /\/auth\/users\/?$/.test(url)
    || /\/auth\/users\/activation\/?$/.test(url)
    || /\/auth\/reengage\/resend-activation\/?$/.test(url)
    || (url || '').includes('/api/public-features')
    || /\/auth\/users\/reset_password\/?$/.test(url)
    || /\/auth\/users\/reset_password_confirm\/?$/.test(url)
    || /\/auth\/o\/(google-oauth2|apple-id)\/?$/.test(url)
  );
}

function isCheckAccessRequest(config) {
  return (config?.url || '').includes('/api/check_access');
}

function isMembershipAccessDenial(error) {
  if (Number(error?.response?.status || 0) !== 403) return false;
  const payload = error?.response?.data || {};
  const reason = `${payload.reason || ''} ${payload.code || ''} ${payload.error || ''}`.toLowerCase();
  return (
    payload.membership_active === false
    || payload.dashboard_requires_membership === true
    || payload.chat_requires_membership === true
    || reason.includes('membership_required')
  );
}

function isDefinitiveRefreshAuthFailure(error) {
  const status = Number(error?.response?.status || 0);
  if (status === 401) return true;
  const detail = `${error?.response?.data?.detail || ''} ${error?.response?.data?.code || ''}`.toLowerCase();
  return detail.includes('invalid refresh') || detail.includes('token_not_valid');
}

function isDashboardBackgroundRequest(config) {
  const url = config?.url || '';
  return (
    url.includes('/api/dashboard-feed')
    || url.includes('/api/dashboard-debug-trace')
    || url.includes('/api/dashboard-hero-feedback')
    || url.includes('/api/dashboard-card-feedback')
    || url.includes('/api/dashboard/cards/')
  );
}

function shouldBroadcastGlobalError(error, config) {
  if (!error) return false;
  if (config?.skipGlobalErrorToast) return false;
  const status = Number(error?.response?.status || 0);
  if (isDashboardBackgroundRequest(config)) return false;
  if (isPublicAuthRequest(config)) return false;
  if (status === 401 || status === 403) return false;
  if (status === 429 || status >= 500) return true;
  return isLikelyTimeoutError(error);
}

apiClient.interceptors.request.use(
  async (config) => {
    if (typeof window !== 'undefined' && window.location?.pathname) {
      config.headers = config.headers || {};
      const reqUrl = String(config?.url || '');
      // Auth CSRF/JWT preflight must not send custom headers corsheaders does not allow.
      // Client-path attribution is only needed on /api/* (marketing incomplete redirect).
      if (/\/api\//.test(reqUrl) || reqUrl.startsWith('api/')) {
        config.headers['X-Dople-Client-Path'] = String(window.location.pathname).slice(0, 500);
      }
    }
    if (
      !isRefreshRequest(config) &&
      !isPublicAuthRequest(config) &&
      !isLogoutRequest(config) &&
      !wasExplicitLogout() &&
      shouldRefreshAccessToken()
    ) {
      try {
        await refreshAuthTokens();
      } catch (error) {
        config._preflightRefreshFailed = true;
        config._preflightRefreshDefinitive = isDefinitiveRefreshAuthFailure(error);
        // Let the request continue without auth headers so the response layer
        // can handle definitive auth failures consistently. Transient refresh
        // failures should not hard-redirect a returning tab to login.
      }
    }

    const accessToken = getAccessToken();
    config.headers = config.headers || {};
    const csrfToken = getCookie('dople_csrf') || getCookie('csrftoken');

    if (accessToken && !config.headers.Authorization && !config.headers.authorization) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    if (csrfToken && !config.headers['X-CSRFToken']) {
      config.headers['X-CSRFToken'] = csrfToken;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const upgradePayload = error.response?.data;
    const originalRequest = error.config || {};
    if (isMembershipAccessDenial(error)) {
      clearCachedAccessStatus();
    }
    if (shouldBroadcastGlobalError(error, originalRequest)) {
      const message = getActionableErrorMessage(error, {
        subject: "request",
        generic: "We hit an unexpected issue. Please try again in a moment.",
      });
      window.dispatchEvent(
        new CustomEvent('dople:global-error', {
          detail: {
            message,
            status: Number(error?.response?.status || 0),
            path: originalRequest?.url || '',
          },
        })
      );
    }

    if (
      error.response?.status === 403 &&
      upgradePayload?.type === 'upgrade' &&
      !originalRequest.skipUpgradeRedirect
    ) {
      sessionStorage.setItem(
        'upgrade_prompt',
        JSON.stringify(upgradePayload)
      );

      window.dispatchEvent(
        new CustomEvent('dople:upgrade-required', {
          detail: upgradePayload,
        })
      );

      // Trip-only offer: no membership upsell. Trip pages show the message
      // themselves; other pages return to Trips.
      if (!window.location.pathname.startsWith('/trips')) {
        try {
          window.location.href = withIncomingSearch('/trips', window.location.search, window.location.hash);
        } catch (redirectError) {
          window.location.href = '/trips';
        }
      }
    }

    if (
      error.response?.status === 401 &&
      !isLogoutRequest(originalRequest) &&
      !originalRequest._retry &&
      !isRefreshRequest(originalRequest) &&
      !isPublicAuthRequest(originalRequest) &&
      !wasExplicitLogout()
    ) {
      originalRequest._retry = true;

      try {
        await refreshAuthTokens();
        const nextAccessToken = getAccessToken();
        originalRequest.headers = originalRequest.headers || {};

        if (nextAccessToken) {
          originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        console.warn('[apiClient] silent refresh failed');
        originalRequest._refreshFailedDefinitive = isDefinitiveRefreshAuthFailure(refreshError);
      }
    }

    if (error.response?.status === 401) {
      if (isLogoutRequest(originalRequest) || isRefreshRequest(originalRequest) || isPublicAuthRequest(originalRequest)) {
        return Promise.reject(error);
      }
      if (wasExplicitLogout()) {
        return Promise.reject(error);
      }
      if (window.location.pathname === '/oauth/callback' && isCheckAccessRequest(originalRequest)) {
        console.warn('[apiClient] check_access failed during OAuth callback; preserving session for retry');
        return Promise.reject(error);
      }
      if (isDashboardBackgroundRequest(originalRequest)) {
        console.warn('[apiClient] dashboard request unauthorized; preserving current session');
        return Promise.reject(error);
      }
      if (
        originalRequest._preflightRefreshFailed &&
        !originalRequest._preflightRefreshDefinitive
      ) {
        console.warn('[apiClient] auth refresh inconclusive before request; preserving current session');
        return Promise.reject(error);
      }
      if (
        originalRequest._retry &&
        originalRequest._refreshFailedDefinitive === false
      ) {
        console.warn('[apiClient] auth refresh inconclusive after 401; preserving current session');
        return Promise.reject(error);
      }
      console.warn('[apiClient] 401 Unauthorized - auto logout');
      // Treat interceptor logout like an explicit session end so the next
      // visit cannot silently restore via a leftover refresh cookie.
      store.dispatch(logout());

      try {
        const loginHref = hardLoginRedirect(
          window.location.pathname,
          window.location.search,
          window.location.hash,
        );
        if (loginHref) window.location.href = loginHref;
      } catch (redirectError) {
        if (!['/login', '/signup', '/oauth/callback'].includes(window.location.pathname)) {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
